import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { post } from "@/lib/api";
import { parseNumber } from "@/lib/format";
import { isTouch } from "@/lib/utils";
import type { CreateOutput } from "@/lib/types";
import { AddressFields, emptyAddress, fetchPlace, isAddressFilled, Suggestions, useSuggest, type Address } from "./shared";

export function CustomerAddDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const session = useMemo(() => crypto.randomUUID(), [open]);
  const [kind, setKind] = useState<"customer" | "lead">("customer");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [rate, setRate] = useState("");
  const [address, setAddress] = useState<Address>(emptyAddress);
  const [linked, setLinked] = useState<{ id: number; name: string } | null>(null);
  const suggest = useSuggest(linked ? "" : name, session);

  function reset() { setKind("customer"); setName(""); setWebsite(""); setRate(""); setAddress(emptyAddress); setLinked(null); onClose(); }

  async function pickPlace(placeId: string) {
    try {
      const d = await fetchPlace(placeId, session);
      setName(d.name);
      setAddress({ street: d.street, zip: d.zip, city: d.city, countryCode: d.countryCode || "AT" });
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const rateValue = parseNumber(rate);
  const valid = name.trim() !== "" && (rate.trim() === "" || (rateValue !== null && rateValue >= 0));
  const create = useMutation({
    mutationFn: () =>
      kind === "lead"
        ? post<CreateOutput>("/api/companies", { name: name.trim(), status: "lead", ...(website.trim() ? { website: website.trim() } : {}), ...(rateValue !== null ? { defaultRate: rateValue } : {}) })
        : post<CreateOutput>("/api/customers", {
            name: name.trim(),
            ...(rateValue !== null ? { defaultRate: rateValue } : {}),
            ...(linked ? { sevdeskContactId: linked.id } : isAddressFilled(address) ? { address } : {}),
          }),
    onSuccess: (res) => {
      for (const key of ["customers", "companies", "clients"]) qc.invalidateQueries({ queryKey: [key] });
      toast.success(`${res.name} angelegt`);
      for (const w of res.warnings) toast.warning(w);
      reset();
      navigate(`/firmen/${res.id}`);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Firma anlegen</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (valid && !create.isPending) create.mutate(); }}>
        <div className="space-y-3">
          <Tabs value={kind} onValueChange={(v) => setKind(v as "customer" | "lead")}>
            <TabsList aria-label="Art"><TabsTrigger value="customer">Kunde</TabsTrigger><TabsTrigger value="lead">Lead</TabsTrigger></TabsList>
          </Tabs>
          <div className="space-y-1.5">
            <Label htmlFor="n">Firmenname</Label>
            <Input id="n" autoFocus={!isTouch} value={name} onChange={(e) => { setName(e.target.value); setLinked(null); }} />
          </div>
          {kind === "customer" && (
            <>
              {linked
                ? <p className="text-sm">Wird mit sevDesk-Kontakt „{linked.name}“ verknüpft, es wird kein neuer Kontakt angelegt. <button type="button" className="underline" onClick={() => setLinked(null)}>Aufheben</button></p>
                : <Suggestions data={suggest.data} onContact={(c) => { setLinked(c); setName(c.name); }} onPlace={pickPlace} />}
              {!linked && <AddressFields value={address} onChange={setAddress} />}
            </>
          )}
          {kind === "lead" && (
            <div className="space-y-1.5">
              <Label htmlFor="w">Website (optional)</Label>
              <Input id="w" type="url" inputMode="url" autoComplete="off" spellCheck={false} placeholder="https://…" value={website} onChange={(e) => setWebsite(e.target.value)} />
              <p className="text-xs text-muted-foreground">Leads brauchen keinen sevDesk-Kontakt; den legst du an, sobald aus dem Lead ein Kunde wird.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="r">Stundensatz (leer = Standard)</Label>
            <Input id="r" inputMode="decimal" className="w-32" value={rate} onChange={(e) => setRate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={reset}>Abbrechen</Button>
          <Button disabled={!valid || create.isPending} type="submit">Anlegen</Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
